/**
 * Hook for managing user settings.
 *
 * Fetches settings on mount and provides update functionality.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  fetchUserSettings,
  updateUserSettings,
  UserSettings,
  UpdateSettingsRequest,
} from '@/lib/api/settings'

interface UseSettingsState {
  settings: UserSettings | null
  loading: boolean
  saving: boolean
  error: string | null
}

export function useSettings() {
  const [state, setState] = useState<UseSettingsState>({
    settings: null,
    loading: true,
    saving: false,
    error: null,
  })

  // Fetch settings on mount
  useEffect(() => {
    let cancelled = false

    fetchUserSettings()
      .then((settings) => {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            settings,
            loading: false,
            error: null,
          }))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[useSettings] Failed to fetch settings:', err)
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load settings',
          }))
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  // Update settings
  const updateSettings = useCallback(
    async (updates: UpdateSettingsRequest): Promise<UserSettings> => {
      setState((prev) => ({ ...prev, saving: true, error: null }))

      try {
        const updated = await updateUserSettings(updates)
        setState((prev) => ({
          ...prev,
          settings: updated,
          saving: false,
        }))
        return updated
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save settings'
        console.error('[useSettings] Failed to update settings:', err)
        setState((prev) => ({
          ...prev,
          saving: false,
          error: message,
        }))
        throw err
      }
    },
    []
  )

  // Refresh settings from server
  const refreshSettings = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const settings = await fetchUserSettings()
      setState((prev) => ({
        ...prev,
        settings,
        loading: false,
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load settings'
      console.error('[useSettings] Failed to refresh settings:', err)
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }))
    }
  }, [])

  return {
    ...state,
    updateSettings,
    refreshSettings,
  }
}
